import { useEffect, useState, useContext } from "react";
import classNames from "classnames";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import {
    fullDateFullYearFormat,
    fullDateLongFormat,
} from "@common/helpers/date";
import { useUpdateFinancialDocumentMetadataMutation } from "@api/financialDocument.api";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { Field } from "@design-system/Field/Field";
import { MonetaryInput } from "@design-system/MonetaryInput/MonetaryInput";
import styles from "./ImporterPage.module.scss";
import { ImporterContext } from "./importer.context";

interface Props {
    financialDocument: FinancialDocumentExtended;
}

export const ImporterBalanceSummary: React.FC<Props> = ({
    financialDocument,
}) => {
    const { revalidateDocument } = useContext(ImporterContext);
    const [openingBalance, setOpeningBalance] = useState<number | null>(
        financialDocument.openingBalance,
    );
    const [closingBalance, setClosingBalance] = useState<number | null>(
        financialDocument.closingBalance,
    );
    const [openingDate, setOpeningDate] = useState(
        financialDocument.openingDate ?? undefined,
    );
    const [closingDate, setClosingDate] = useState(
        financialDocument.closingDate ?? undefined,
    );

    useEffect(() => {
        setOpeningBalance(financialDocument.openingBalance);
        setClosingBalance(financialDocument.closingBalance);
        setOpeningDate(financialDocument.openingDate ?? undefined);
        setClosingDate(financialDocument.closingDate ?? undefined);
    }, [financialDocument]);

    const updateDocumentMetadata =
        useUpdateFinancialDocumentMetadataMutation(financialDocument);

    const revalidateAfterMetadataUpdate = () => {
        revalidateDocument({ financialDocumentId: financialDocument.id });
    };

    const updateDate = (
        date: string | undefined,
        field: "openingDate" | "closingDate",
    ) => {
        if (field === "openingDate") {
            setOpeningDate(date);
            updateDocumentMetadata.mutate(
                { openingDate: date },
                { onSuccess: revalidateAfterMetadataUpdate },
            );
        } else {
            setClosingDate(date);
            updateDocumentMetadata.mutate(
                { closingDate: date },
                { onSuccess: revalidateAfterMetadataUpdate },
            );
        }
    };

    const saveBalance = (field: "openingBalance" | "closingBalance") => {
        if (
            field === "openingBalance" &&
            openingBalance !== financialDocument.openingBalance
        ) {
            updateDocumentMetadata.mutate(
                { openingBalance },
                { onSuccess: revalidateAfterMetadataUpdate },
            );
        }
        if (
            field === "closingBalance" &&
            closingBalance !== financialDocument.closingBalance
        ) {
            updateDocumentMetadata.mutate(
                { closingBalance },
                { onSuccess: revalidateAfterMetadataUpdate },
            );
        }
    };

    const isOpeningDateMissing = !openingDate;
    const isOpeningBalanceMissing = openingBalance === null;
    const isClosingDateMissing = !closingDate;
    const isClosingBalanceMissing = closingBalance === null;

    return (
        <section className={styles.balanceSummary}>
            <Field label="Opening balance" className={styles.balanceField}>
                <div className="input-group">
                    <DatePicker
                        mode="day"
                        variant="input"
                        showTriggerIcon={false}
                        clearable={false}
                        value={openingDate}
                        onValueChange={(date) =>
                            updateDate(date, "openingDate")
                        }
                        inputFormat={fullDateLongFormat}
                        displayFormat={fullDateFullYearFormat}
                        className={classNames(styles.dateField, {
                            [styles.invalidDateField]: isOpeningDateMissing,
                        })}
                    />
                    <MonetaryInput
                        value={openingBalance ?? undefined}
                        onValueChange={(v) => setOpeningBalance(v ?? null)}
                        size="xs"
                        onBlur={() => saveBalance("openingBalance")}
                        className={classNames(styles.balanceInput, {
                            [styles.invalidInput]: isOpeningBalanceMissing,
                        })}
                    />
                </div>
            </Field>

            <Field label="Closing balance" className={styles.balanceField}>
                <div className="input-group">
                    <DatePicker
                        mode="day"
                        variant="input"
                        showTriggerIcon={false}
                        clearable={false}
                        value={closingDate}
                        onValueChange={(date) =>
                            updateDate(date, "closingDate")
                        }
                        inputFormat={fullDateLongFormat}
                        displayFormat={fullDateFullYearFormat}
                        className={classNames(styles.dateField, {
                            [styles.invalidDateField]: isClosingDateMissing,
                        })}
                    />
                    <MonetaryInput
                        value={closingBalance ?? undefined}
                        onValueChange={(v) => setClosingBalance(v ?? null)}
                        size="xs"
                        onBlur={() => saveBalance("closingBalance")}
                        className={classNames(styles.balanceInput, {
                            [styles.invalidInput]: isClosingBalanceMissing,
                        })}
                    />
                </div>
            </Field>
        </section>
    );
};
